import type { Participant } from '@seen/core/models/patients/participant'
import { format, parseISO } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'

// Custom type for extension status and API
declare global {
  interface Window {
    seenSidecarLoaded?: boolean
    seenSidecar?: {
      isAvailable: boolean
      openAstranaSearch: (params: {
        memberId?: string
        dateOfBirth?: string
        authorizationId?: string
      }) => boolean
    }
    seenSidecarEvents?: EventTarget
  }
}

interface UseAstranaAuthorizationSearchResult {
  handleSearch: () => void
  hasValidMemberSearch: boolean
  hasValidAuthSearch: boolean
  extensionDetected: boolean
}

export const useAstranaAuthorizationSearch = (
  participant?: Participant,
  authorizationId?: string
): UseAstranaAuthorizationSearchResult => {
  const memberId = !authorizationId ? (participant?.seenMRN ?? '') : undefined
  const birthDate = useMemo(
    () =>
      !authorizationId && participant?.birthDate
        ? format(parseISO(participant.birthDate), 'MM/dd/yyyy')
        : undefined,
    [participant?.birthDate, authorizationId]
  )

  const [extensionDetected, setExtensionDetected] = useState(false)

  useEffect(() => {
    if (window.seenSidecar?.isAvailable) {
      setExtensionDetected(true)
      return
    }

    const handleExtensionReady = (): void => {
      setExtensionDetected(true)
    }
    window.addEventListener('seenSidecarReady', handleExtensionReady)

    const handleExtensionMessage = (event: MessageEvent): void => {
      if (event.data?.type === 'SEEN_SIDECAR_LOADED') {
        setExtensionDetected(true)
      }
    }
    window.addEventListener('message', handleExtensionMessage)

    return () => {
      window.removeEventListener('seenSidecarReady', handleExtensionReady)
      window.removeEventListener('message', handleExtensionMessage)
    }
  }, [])

  const handleSearch = useCallback(() => {
    if (authorizationId) {
      window.open(
        `https://ayce.astranahealth.com/create_auth?ipa=APC&authno=${authorizationId}`,
        '_blank'
      )
    }

    const searchParams = authorizationId
      ? { authorizationId }
      : { memberId, dateOfBirth: birthDate }

    if (window.seenSidecar?.isAvailable) {
      window.seenSidecar.openAstranaSearch(searchParams)
      return
    }

    if (extensionDetected) {
      window.postMessage(
        {
          ...searchParams,
          type: 'ASTRANA_SEARCH_REQUEST',
        },
        window.location.origin
      )
    }
  }, [memberId, birthDate, authorizationId, extensionDetected])

  const hasValidMemberSearch = !!(!authorizationId && memberId && birthDate)
  const hasValidAuthSearch = !!authorizationId

  return {
    handleSearch,
    hasValidMemberSearch,
    hasValidAuthSearch,
    extensionDetected,
  }
}
